import { PrivateFlowVerifyType, SupportedLanguage, UserType } from "interfaces";
import { isEmpty } from "lodash";
import i18n from "utills/i18n";
import { z } from "zod";

export const languageSchema = (
  languages: SupportedLanguage[],
  optional = false,
) => {
  return z.object({
    en:
      languages.includes(SupportedLanguage.EN) && !optional
        ? z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired"))
        : z.string().optional().nullable(),
    vi:
      languages.includes(SupportedLanguage.VI) && !optional
        ? z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired"))
        : z.string().optional().nullable(),
  });
};

export const AdditionalSetupSchema = (
  languages: SupportedLanguage[],
  userType?: UserType,
) => {
  return z.object({
    basic_information: z
      .object({
        is_have_participant_list: z.boolean(),
        is_hide_vat: z.boolean(),
        display_group_button: z.boolean(),
        show_bib: z.boolean(),
        is_display_on_the_organization_detail_page: z.boolean(),
        event_term_url: languageSchema(languages),
        hold_ticket_time: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired",
            ),
          })
          .min(0, {
            message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        max_tickets_to_buy: z
          .union([
            z
              .number()
              .int()
              .min(0, i18n.t("validation.errorMessage.minNum", { min: 0 }))
              .nullable(),
            z.nan(),
          ])
          .optional(),
        order_min_qty_ticket: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired",
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        order_max_qty_ticket: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired",
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        note_individual_ticket_purchase_flow: languageSchema(languages, true),
        note_group_ticket_purchase_flow: languageSchema(languages, true),
        ...(userType === UserType.ADMIN && {
          has_addon: z.boolean(),
          use_pass_tool: z.boolean(),
          use_authentication: z.boolean(),
          is_static_html: z.boolean(),
          static_html_content: z.string().optional().nullable(),
        }),
      })
      .refine(
        (data) => data.order_min_qty_ticket <= data.order_max_qty_ticket,
        {
          message: i18n.t("validation.errorMessage.minNum", {
            min: i18n.t(
              "eventSetup.additionalSetup.basicInformation.minTicketsQuantityPerOrder",
            ),
          }),
          path: ["order_max_qty_ticket"],
        },
      ),
    change_registration_information: z
      .discriminatedUnion("is_change_registration", [
        z.object({
          is_change_registration: z.literal(false),
          start_change_info_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          end_change_info_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          max_number_of_times_to_update_information: z.union([
            z.literal(0),
            z.coerce.number(),
          ]),
        }),
        z.object({
          is_change_registration: z.literal(true),
          start_change_info_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          end_change_info_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          max_number_of_times_to_update_information: z
            .number({
              invalid_type_error: i18n.t(
                "validation.errorMessage.mustBeANumber",
              ),
              required_error: i18n.t(
                "validation.errorMessage.thisFieldIsRequired",
              ),
            })
            .min(1, {
              message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
            })
            .nonnegative({
              message: i18n.t("validation.errorMessage.nonNegativeNum"),
            }),
        }),
      ])
      .superRefine((value, ctx) => {
        if (
          (value.end_change_info_date ?? "") <
            (value.start_change_info_date ?? "") &&
          value.is_change_registration === true
        ) {
          ctx.addIssue({
            path: ["end_change_info_date"],
            code: "custom",
            message: i18n.t("validation.errorMessage.endDateCannotBeEarlier"),
          });
        }
      }),
    change_distance: z
      .discriminatedUnion("is_change_distance", [
        z.object({
          is_change_distance: z.literal(false),
          start_change_distance_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          end_change_distance_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          max_number_of_times_to_change_distance: z.union([
            z.literal(0),
            z.coerce.number(),
          ]),
          change_distance_fee: z.union([z.literal(0), z.coerce.number()]),
        }),
        z.object({
          is_change_distance: z.literal(true),
          start_change_distance_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          end_change_distance_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          max_number_of_times_to_change_distance: z
            .number({
              invalid_type_error: i18n.t(
                "validation.errorMessage.mustBeANumber",
              ),
              required_error: i18n.t(
                "validation.errorMessage.thisFieldIsRequired",
              ),
            })
            .min(0, {
              message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
            })
            .nonnegative({
              message: i18n.t("validation.errorMessage.nonNegativeNum"),
            }),
          change_distance_fee: z
            .number({
              invalid_type_error: i18n.t(
                "validation.errorMessage.mustBeANumber",
              ),
              required_error: i18n.t(
                "validation.errorMessage.thisFieldIsRequired",
              ),
            })
            .min(0, {
              message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
            })
            .nonnegative({
              message: i18n.t("validation.errorMessage.nonNegativeNum"),
            }),
        }),
      ])
      .superRefine((value, ctx) => {
        if (
          (value.end_change_distance_date ?? "") <
            (value.start_change_distance_date ?? "") &&
          value.is_change_distance === true
        ) {
          ctx.addIssue({
            path: ["end_change_distance_date"],
            code: "custom",
            message: i18n.t("validation.errorMessage.endDateCannotBeEarlier"),
          });
        }
      }),
    transfer_ticket: z
      .discriminatedUnion("is_transfer_ticket", [
        z.object({
          is_transfer_ticket: z.literal(false),
          start_transfer_ticket_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          end_transfer_ticket_date: z
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value)),
          max_number_of_times_to_transfer_ticket: z.union([
            z.literal(0),
            z.coerce.number(),
          ]),
          transfer_ticket_fee: z.union([z.literal(0), z.coerce.number()]),
        }),
        z.object({
          is_transfer_ticket: z.literal(true),
          start_transfer_ticket_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          end_transfer_ticket_date: z
            .string()
            .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          max_number_of_times_to_transfer_ticket: z
            .number({
              invalid_type_error: i18n.t(
                "validation.errorMessage.mustBeANumber",
              ),
              required_error: i18n.t(
                "validation.errorMessage.thisFieldIsRequired",
              ),
            })
            .min(0, {
              message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
            })
            .nonnegative({
              message: i18n.t("validation.errorMessage.nonNegativeNum"),
            }),
          transfer_ticket_fee: z
            .number({
              invalid_type_error: i18n.t(
                "validation.errorMessage.mustBeANumber",
              ),
              required_error: i18n.t(
                "validation.errorMessage.thisFieldIsRequired",
              ),
            })
            .min(0, {
              message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
            })
            .nonnegative({
              message: i18n.t("validation.errorMessage.nonNegativeNum"),
            }),
        }),
      ])
      .superRefine((value, ctx) => {
        if (
          (value.end_transfer_ticket_date ?? "") <
            (value.start_transfer_ticket_date ?? "") &&
          value.is_transfer_ticket === true
        ) {
          ctx.addIssue({
            path: ["end_transfer_ticket_date"],
            code: "custom",
            message: i18n.t("validation.errorMessage.endDateCannotBeEarlier"),
          });
        }
      }),
    private_flow: z
      .object({
        is_use: z.boolean(),
        verify_type: z
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value)),
        usage_count: z.preprocess(
          (val) => {
            if (val === "" || val === undefined || val === null) {
              return null;
            }
            return Number(val);
          },
          z
            .number({
              invalid_type_error: "Please input a number",
            })
            .nullable()
            .optional(),
        ),
        sync_enabled: z.boolean(),
        passcode_source_event_ids: z.array(z.string()).optional(),
        email_domain: z.string().transform((value) => {
          return value
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line !== "")
            .join("\n");
        }),
      })
      .superRefine((value, ctx) => {
        if (value.is_use) {
          if (isEmpty(value.verify_type)) {
            ctx.addIssue({
              path: ["verify_type"],
              code: z.ZodIssueCode.custom,
              message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
            });
          }
          if (value.verify_type === PrivateFlowVerifyType.DOMAIN_EMAIL) {
            const lines = (value.email_domain || "").split("\n");

            const domainRegex = /^@([\w-]+\.)+[a-zA-Z]{2,}$/;
            const invalidDomains = [];

            for (let i = 0; i < lines.length; i++) {
              const domain = lines[i].trim();
              if (!domainRegex.test(domain)) {
                invalidDomains.push(`"${domain}" (line ${i + 1})`);
              }
            }
            if (invalidDomains.length > 0) {
              ctx.addIssue({
                path: ["email_domain"],
                code: z.ZodIssueCode.custom,
                message: `Invalid domain: ${invalidDomains.join(", ")}.`,
              });
            }
          }
          if (value.sync_enabled) {
            if (isEmpty(value.passcode_source_event_ids)) {
              ctx.addIssue({
                path: ["passcode_source_event_ids"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
              });
            }
            if (
              typeof value.usage_count !== "number" ||
              isNaN(value.usage_count)
            ) {
              ctx.addIssue({
                path: ["usage_count"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.mustBeANumber"),
              });
              return;
            }
            if (value.usage_count < 0) {
              ctx.addIssue({
                path: ["usage_count"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
              });
            }
          }
        }
      }),
    payment_method: z.object({
      ignore_payment_methods: z
        .array(z.string())
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      ...(userType === UserType.ADMIN && {
        payment_gateway_promotion_id: z.string().optional(),
        order_code_prefix: z
          .string()
          .trim()
          .max(10, {
            message: i18n.t("error-max-length", { max: 10 }),
          })
          .regex(/^[a-zA-Z0-9]*$/, {
            message: i18n.t("error-special-character"),
          })
          .optional()
          .nullable(),
      }),
    }),
    legal_info: z.object({
      company_name: z.string().optional().or(z.literal("")),
      address: z.string().optional().or(z.literal("")),
      tax_code: z.string().optional().or(z.literal("")),
      phone: z.string().optional().or(z.literal("")),
      representative: z.string().optional().or(z.literal("")),
      position: z.string().optional().or(z.literal("")),
      account_name: z.string().optional().or(z.literal("")),
      account_number: z.string().optional().or(z.literal("")),
      bank: z.string().optional().or(z.literal("")),
      contract_number: z.string().optional().or(z.literal("")),
      service_fee: z
        .union([
          z.preprocess(
            (val) => (val === "" || val === null ? null : Number(val)),
            z
              .number()
              .min(0, "Value must be between 0 and 100")
              .max(100, "Value must be between 0 and 100")
              .nullable(),
          ),
          z.nan(),
        ])
        .optional(),
      vat: z
        .union([
          z.preprocess(
            (val) => (val === "" || val === null ? null : Number(val)),
            z
              .number()
              .min(0, "Value must be between 0 and 100")
              .max(100, "Value must be between 0 and 100")
              .nullable(),
          ),
          z.nan(),
        ])
        .optional(),
    }),
    einvoice: z
      .object({
        is_einvoice_enabled: z.boolean(),
        label_event_on_einvoice: z
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value)),
      })
      .superRefine((value, ctx) => {
        if (value.is_einvoice_enabled) {
          if (isEmpty(value.label_event_on_einvoice)) {
            ctx.addIssue({
              path: ["label_event_on_einvoice"],
              code: z.ZodIssueCode.custom,
              message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
            });
          }
        }
      }),
  });
};
